import React from "react";
import { Link, Navigate } from "react-router-dom";
import { Crown, Gift, Clock } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { MemberShell } from "@/components/MemberShell";
import { useI18n } from "@/lib/i18n";
import { useMember } from "@/lib/member";
import { rp } from "@/lib/api";
import { TierChip } from "@/components/TierChip";

const Stat = ({ label, value, testid }) => (
  <div className="lm-card rounded-xl p-5">
    <div className="text-[11px] uppercase tracking-widest text-ink-soft">{label}</div>
    <div className="mt-1.5 font-display text-2xl tracking-tight" data-testid={testid}>{value}</div>
  </div>
);

export default function MemberDashboard() {
  const { t } = useI18n();
  const { member, loading } = useMember();
  if (loading) return <SiteLayout><div className="mx-auto max-w-4xl px-5 py-24 text-ink-soft">{t("loading")}</div></SiteLayout>;
  if (!member) return <Navigate to="/member/login" replace />;
  if (!member.profile_complete) return <Navigate to="/member/profile" replace />;

  const c = member.customer;
  const l = member.loyalty || {};
  const left = Math.max(0, (l.required || 10) - (l.progress || 0));
  const pct = Math.min(100, Math.round(((l.progress || 0) / (l.required || 10)) * 100));

  return (
    <MemberShell>
      {member.link_status === "pending" && (
        <div className="mb-8 rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="pending-link-notice">
          <div className="flex items-center gap-2 font-semibold text-amber-900"><Clock size={16} /> {t("pendingLinkTitle")}</div>
          <p className="mt-1.5 text-sm text-amber-900/80">{t("pendingLinkSub")}</p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="lm-card rounded-xl p-5">
          <div className="text-[11px] uppercase tracking-widest text-ink-soft">{t("currentTier")}</div>
          <div className="mt-2 flex items-center gap-2">
            <Crown size={18} className="text-lm-gold" />
            <TierChip tier={c?.tier || "Bronze"} percent={c?.discount_percent || 0} />
          </div>
          <div className="mt-2 text-xs text-ink-soft">
            {(c?.discount_percent || 0) > 0 ? `${c.discount_percent}% ${t("memberDiscount")}` : t("noDiscountYet")}
          </div>
        </div>
        <Stat label={t("completedRentals")} value={c?.completed_rentals ?? 0} testid="member-completed" />
        <Stat label={t("totalSpend")} value={rp(c?.total_spending || 0)} testid="member-spend" />
      </div>

      <div className="mt-4 lm-card rounded-xl p-5" data-testid="loyalty-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-semibold"><Gift size={16} className="text-lm-red" /> {t("loyaltyProgress")}</div>
          <div className="text-sm text-ink-soft" data-testid="loyalty-progress">{l.progress || 0} / {l.required || 10}</div>
        </div>
        <div className="mt-3 h-2 rounded-full bg-black/10 overflow-hidden">
          <div className="h-full bg-lm-red transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-2.5 text-xs text-ink-soft">
          {left === 0 ? t("rewardReady") : t("awayFromReward").replace("{n}", left)}
        </div>
        <div className="mt-3 text-sm">{t("availableRewards")}: <span data-testid="rewards-count" className="font-semibold">{l.rewards_available || 0}</span></div>
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/book" data-testid="member-book-btn" className="h-11 px-6 rounded-full bg-lm-red text-white text-sm grid place-items-center hover:bg-ink transition-colors">{t("bookNow")}</Link>
        <Link to="/member/bookings" className="h-11 px-6 rounded-full border border-black/15 text-sm grid place-items-center hover:border-lm-red">{t("myBookings")}</Link>
        <Link to="/member/addresses" className="h-11 px-6 rounded-full border border-black/15 text-sm grid place-items-center hover:border-lm-red">{t("savedAddresses")}</Link>
      </div>
    </MemberShell>
  );
}
